import { useState } from "react";
import type { OpenCollaborationInsightsFunnel, TargetCollaborationInsightsFunnel } from "@/api/collaborations";
import { Skeleton } from "@/components/ui/skeleton";
import { ChartTooltip } from "./chart-tooltip";

type Props = {
  data: TargetCollaborationInsightsFunnel | OpenCollaborationInsightsFunnel | null | undefined;
  isLoading: boolean;
  /** Currently-selected funnel row key (controlled by the panel). */
  selectedKey?: string | null;
  /** Called when a bar (or its label/count) is clicked. Pass `null` to clear. */
  onSelect?: (key: string | null) => void;
  /**
   * Total creators in the collaboration (from the summary KPIs).
   * When provided, bar widths are relative to this value so 100% means
   * "all creators in this collaboration", matching the left-panel count.
   */
  totalCreators?: number;
};

const ROW_LABELS: Record<string, string> = {
  invited: "Invited",
  sample_requested: "Sample Requested",
  sample_status: "Sample Status",
  pending_post: "Pending Post",
  posted_no_gmv: "Posted No GMV",
  generated_gmv: "Generated GMV",
  cancelled: "Cancelled",
};

const ROW_TOOLTIPS: Record<string, (count: number) => string> = {
  invited: (n) => `${n.toLocaleString()} creator${n === 1 ? "" : "s"} have been invited.`,
  sample_requested: (n) => `${n.toLocaleString()} creator${n === 1 ? "" : "s"} requested a sample.`,
  sample_status: (n) => `${n.toLocaleString()} creator${n === 1 ? "" : "s"} are awaiting / receiving their sample.`,
  pending_post: (n) => `${n.toLocaleString()} creator${n === 1 ? "" : "s"} received a sample but haven't posted yet.`,
  posted_no_gmv: (n) => `${n.toLocaleString()} creator${n === 1 ? "" : "s"} posted content but haven't generated GMV.`,
  generated_gmv: (n) => `${n.toLocaleString()} creator${n === 1 ? "" : "s"} posted and generated GMV.`,
  cancelled: (n) => `${n.toLocaleString()} cancelled application${n === 1 ? "" : "s"}.`,
};

const TRACK_STYLE: React.CSSProperties = {
  height: 25,
  width: "100%",
  borderRadius: 4,
  background: "rgba(255,255,255,0.08)",
  overflow: "hidden",
  position: "relative",
};

export function InsightsFunnelTab({ data, isLoading, selectedKey = null, onSelect, totalCreators }: Props) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const hasHover = hoveredKey !== null;
  const hasSelection = selectedKey !== null;

  if (isLoading) {
    return (
      <div className="flex flex-col">
        <div className="mb-5">
          <Skeleton className="h-5 w-36 bg-white/20" />
          <Skeleton className="h-3 w-72 bg-white/10 mt-1.5" />
        </div>
        <div className="flex flex-col gap-3">
          {(
            ["invited", "sample_requested", "sample_status", "pending_post", "posted_no_gmv", "cancelled"] as const
          ).map((key) => (
            <div key={key} className="flex items-center gap-3">
              <Skeleton className="h-3 w-28 bg-white/20 shrink-0" />
              <div style={{ ...TRACK_STYLE, flex: 1 }}>
                <Skeleton className="h-full w-1/3 bg-white/20" style={{ borderRadius: 4 }} />
              </div>
              <Skeleton className="h-3 w-6 bg-white/20 shrink-0" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const header = (
    <div className="mb-5">
      <p className="text-base font-medium text-white">Overview Funnel</p>
      <p className="text-sm text-white/50 mt-0.5">
        Here is a breakdown of your relationship with the creators who are in this collaboration
      </p>
    </div>
  );

  if (!data || data.rows.length === 0) {
    return (
      <>
        {header}
        <div className="text-sm text-white/50 text-center py-6">No funnel data available</div>
      </>
    );
  }

  // Use total creators as the scale so bars represent share of the full
  // collaboration. Fall back to the row maximum if no total is available.
  const maxCount = Math.max(totalCreators ?? 0, ...data.rows.map((r) => r.count), 1);

  return (
    <div className="flex flex-col">
      {header}
      <div className="flex flex-col gap-3">
        {data.rows.map((row) => {
          const pct = (row.count / maxCount) * 100;
          const isHovered = hoveredKey === row.key;
          const isSelected = selectedKey === row.key;
          const active = isHovered || isSelected;
          const dimmed = hasHover ? !isHovered && !isSelected : hasSelection ? !isSelected : false;
          const label = ROW_LABELS[row.key] ?? row.key;
          const interactive = !!onSelect;

          const TrackTag = interactive ? "button" : "div";
          const barStyle: React.CSSProperties = {
            height: "100%",
            width: `${pct}%`,
            borderRadius: 4,
            background: "linear-gradient(90deg, #D5A9FF 0%, #FFBF8B 100%)",
            boxShadow: isSelected
              ? "0 0 12px rgba(213,169,255,0.55), 0 2px 6px rgba(213,169,255,0.3)"
              : isHovered
                ? "0 2px 6px 0 rgba(213,169,255,0.28)"
                : "none",
            transform: active ? "scaleY(1.1)" : "scaleY(1)",
            filter: isSelected ? "brightness(1.15)" : "brightness(1)",
            transformOrigin: "center",
            transition:
              "box-shadow 200ms ease, transform 200ms ease, filter 200ms ease, width 600ms cubic-bezier(0.22,1,0.36,1)",
          };

          return (
            <div
              key={row.key}
              onMouseEnter={() => setHoveredKey(row.key)}
              onMouseLeave={() => setHoveredKey(null)}
              role="presentation"
              className={`flex items-center gap-3 transition-opacity duration-200 ${
                dimmed ? "opacity-35" : "opacity-100"
              }`}
            >
              <span
                className={`text-xs w-32 shrink-0 transition-[color,font-weight] ${
                  active ? "text-white font-semibold" : "text-white/70"
                }`}
              >
                {label}
              </span>
              {/* Wrapper holds the tooltip OUTSIDE the track's overflow:hidden box. */}
              <div className="relative flex-1">
                {isHovered && <ChartTooltip>{ROW_TOOLTIPS[row.key]?.(row.count) ?? label}</ChartTooltip>}
                <TrackTag
                  type={interactive ? "button" : undefined}
                  style={TRACK_STYLE}
                  onClick={interactive ? () => onSelect?.(isSelected ? null : row.key) : undefined}
                  aria-pressed={interactive ? isSelected : undefined}
                  className={interactive ? "cursor-pointer outline-none w-full p-0 border-0" : undefined}
                >
                  <div style={barStyle} />
                </TrackTag>
              </div>
              <span
                className={`text-xs w-8 text-right shrink-0 transition-[color,font-weight] ${
                  active ? "text-white font-bold" : "text-white font-semibold"
                }`}
              >
                {row.count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
